import { Toaster, useRouter } from '@main/core-ui';
import { privacyCenterCustomizableMessages } from '@main/pc-types';
import ErrorBoundary from '@main/privacy-center/src/containers/ErrorBoundary';
import FooterWithPath from '@main/privacy-center/src/containers/FooterWithPath';
import VerifyLoginEmail from '@main/privacy-center/src/containers/VerifyLoginEmail/Loadable';
import VerifyRequestEmail from '@main/privacy-center/src/containers/VerifyRequestEmail/Loadable';
import VerifyRequestPhone from '@main/privacy-center/src/containers/VerifyRequestPhone/Loadable';
import WrongLoginEmail from '@main/privacy-center/src/containers/WrongLoginEmail/Loadable';
import WrongRequestEmail from '@main/privacy-center/src/containers/WrongRequestEmail/Loadable';
import WrongRequestPhone from '@main/privacy-center/src/containers/WrongRequestPhone/Loadable';
import { PrivacyCenterGlobalStyles } from '@main/privacy-center/src/global-styles';
import { EMPTY_THEME_INPUT } from '@main/privacy-center/src/theme';
import React from 'react';
import { Helmet } from 'react-helmet';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { ThemeProvider } from 'styled-components';

import { DraftPrivacyCenterBanner } from '../DraftPrivacyCenterBanner';
import { selectRequiredPrivacyCenter } from './selectors';
import { AppWrapper, FooterWrapper } from './wrappers';

/**
 * This component is the skeleton around the actual pages, and should only
 * contain code that should be seen on all pages. (e.g. navigation bar)
 */
export const App: React.FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const {
    organization: { name },
    home,
    theme,
    supportEmail,
  } = useSelector(selectRequiredPrivacyCenter);
  const { formatMessage } = useIntl();
  const {
    queryParams: { modal },
  } = useRouter();

  return (
    <ThemeProvider theme={{ ...EMPTY_THEME_INPUT, ...theme }}>
      <ErrorBoundary>
        <Helmet>
          <meta name="theme-color" content={theme.colors.primary} />
          <meta
            content={formatMessage(
              privacyCenterCustomizableMessages.metaDescription,
              {
                organizationName: name,
              },
            )}
            name="description"
          />
          <title>
            {formatMessage(privacyCenterCustomizableMessages.metaTitle, {
              organizationName: name,
            })}
          </title>
        </Helmet>
        {/* Hide app contents from screen readers when modal active */}
        <AppWrapper {...(modal ? { 'aria-hidden': true } : {})}>
          <DraftPrivacyCenterBanner />
          <PrivacyCenterGlobalStyles />
          <VerifyRequestEmail />
          <WrongRequestEmail />
          <VerifyLoginEmail />
          <VerifyRequestPhone />
          <WrongRequestPhone />
          <WrongLoginEmail />
          {children}
          <FooterWrapper>
            <FooterWithPath
              homepageUrl={formatMessage(home)}
              supportEmail={supportEmail}
            />
          </FooterWrapper>
          <Toaster />
        </AppWrapper>
      </ErrorBoundary>
    </ThemeProvider>
  );
};

export default App;
